import { NoteCharMusic, NoteCharVoice } from "./types";

/**
 * 柱式和弦的语法解析。
 *
 * 语法：方括号内并列若干音，各音之间用空格、逗号、顿号或分号分隔（相邻的纯数字
 * 也可以直接连写）：
 *
 * ```
 * [135]          1 3 5 同时按
 * [1 3 5]        同上，带分隔符更易读
 * [1,3,5]        同上
 * [1d,3d,5d]     三个音都低八度
 * [#4,6,1]       升四、六、高八度一
 * ```
 *
 * ⚠️ 这里刻意**不认 `/` 作分隔符**：`[4/4]` 是小节线拍号属性，一旦把 `/` 当作音的分隔，
 * 拍号就会被解析成「4 与 4 两个音」的和弦，整行的拍号属性全部失效。
 *
 * 返回 `undefined` 表示「这段内容不是和弦」——调用方应当把它继续当属性处理。这是
 * 与既有语法共存的关键：`[4/4]`、`[3.]`、`[w=2]`、`[brk]` 全都会走到这条分支上。
 */

/** 可以作为音名的字符，与 `noteCharChecker` 保持一致 */
const NOTE_CHARS = '0123456789RSTXYZ_'

/** 允许作为音之间分隔的字符 */
const SEPARATORS = ' \t,，、;；'

/** 变化音记号 */
const ACCIDENTALS: {[_: string]: number} = { '#': 1, 'b': -1, '=': 0, '$': 0.5, '%': -0.5 }

/**
 * 解析柱式和弦的括号内容。
 *
 * @returns 至少两个音时返回音列表（第一个音是主音，仍然是音符本身的 char/octave/delta）；
 *          内容不是和弦时返回 `undefined`
 */
export function parseChordVoices(text: string): NoteCharVoice[] | undefined {
	// 括号文本可能带外侧方括号，统一削掉
	let body = text
	if(body.startsWith('[') && body.endsWith(']')) {
		body = body.substring(1, body.length - 1)
	}
	const voices: NoteCharVoice[] = []
	let index = 0
	while(index < body.length) {
		// 跳过分隔符
		if(SEPARATORS.indexOf(body[index]) != -1) {
			index += 1
			continue
		}
		// 变化音前缀（可叠加：#、bb、## ...）
		let delta = NaN
		while(index < body.length && ACCIDENTALS[body[index]] !== undefined) {
			if(delta != delta) {
				delta = 0
			}
			delta += ACCIDENTALS[body[index]]
			index += 1
		}
		// 音名
		if(index >= body.length || NOTE_CHARS.indexOf(body[index]) == -1) {
			return undefined
		}
		const char = body[index]
		index += 1
		// 八度后缀
		let octave = 0
		while(index < body.length && (body[index] == 'd' || body[index] == 'e')) {
			octave += (body[index] == 'e' ? 1 : -1)
			index += 1
		}
		voices.push({ char, octave, delta, finalDelta: NaN })
	}
	// 单音不是和弦 —— `[1]` 之类仍按属性解释
	if(voices.length < 2) {
		return undefined
	}
	return voices
}

/**
 * 把和弦的从属音包装成普通音符字符，供音高计算等只认 `NoteCharMusic` 的地方复用
 */
export function chordVoiceAsNoteChar(voice: NoteCharVoice): NoteCharMusic {
	return {
		type: 'music',
		sampler: 'music',
		char: voice.char,
		octave: voice.octave,
		delta: voice.delta,
		finalDelta: voice.finalDelta,
	}
}

/**
 * 柱式和弦相邻两行的行距，单位是「音符行高」（`measureNoteChar()[1]`）的倍数。
 *
 * 排版（`MusicPaint.drawMusicNote`）与音符高亮框（`SectionsRenderer`）必须用同一个
 * 值：高亮框要罩住整摞从属音，行距一旦两边不一致，框就会短一截或多出一截，
 * 而这两处代码隔着一个文件、改一处另一处不会报错 —— 所以提成常量。
 */
export const CHORD_ROW_SPACE_RATIO = 0.95

/**
 * 和弦相对主音的叠放层数（0 = 只有主音）。
 *
 * 排版时每一层占一行，行高统计要用它。
 */
export function chordStackRows(char: NoteCharMusic): number {
	return char.voices && char.voices.length > 0 ? char.voices.length : 0
}
